import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectCharactersByFamily, selectFamilyById } from './charactersSlice';
import FamilyTreeView from './FamilyTreeView';
import useThemeStyles from '../../hooks/useThemeStyles';

/**
 * Transforms characters from the app's format (id, displayName) to FamilyTree format (_id, name)
 * and builds connections array from family.connections
 */
function transformCharactersForTree(characters, family) {
  if (!Array.isArray(characters) || !family) return [];

  // Build a map of character IDs to character objects for quick lookup
  const charMap = new Map();
  characters.forEach((char) => {
    charMap.set(char.id, char);
  });

  // Build connections map: characterId -> array of connections
  const connectionsByCharId = new Map();

  // Process family.connections to build character.connections
  // family.connections format: { from: charId, to: charId, type: 'parent'|'child'|'spouse'|... }
  // Interpretation: if type='parent', then 'from' is parent of 'to'
  if (Array.isArray(family.connections)) {
    family.connections.forEach((conn) => {
      const fromChar = charMap.get(conn.from);
      const toChar = charMap.get(conn.to);

      if (!fromChar || !toChar) return;

      // Map connection type to relationToSelf from the target character's perspective
      // relationToSelf represents the relationship FROM the character's perspective TO the target
      let relationToSelf = '';
      switch (conn.type) {
        case 'parent':
          // from is parent of to
          // From 'to's perspective: 'to' has a PARENT ('from')
          // So 'to' should have a connection with relationToSelf='PARENT' pointing to 'from'
          relationToSelf = 'PARENT';
          // Add connection to 'to' character
          if (!connectionsByCharId.has(conn.to)) {
            connectionsByCharId.set(conn.to, []);
          }
          connectionsByCharId.get(conn.to).push({
            _id: `${conn.from}-${conn.to}-${conn.id || Date.now()}`,
            targetCharacterId: conn.from,
            targetCharacterName: fromChar.displayName || fromChar.name || 'Unknown',
            relationToSelf: 'PARENT',
          });
          break;
        case 'child':
          // from is child of to (so to is parent of from)
          // From 'to's perspective: 'to' has a CHILD ('from')
          // So 'to' should have a connection with relationToSelf='CHILD' pointing to 'from'
          relationToSelf = 'CHILD';
          // Add connection to 'to' character
          if (!connectionsByCharId.has(conn.to)) {
            connectionsByCharId.set(conn.to, []);
          }
          connectionsByCharId.get(conn.to).push({
            _id: `${conn.from}-${conn.to}-${conn.id || Date.now()}`,
            targetCharacterId: conn.from,
            targetCharacterName: fromChar.displayName || fromChar.name || 'Unknown',
            relationToSelf: 'CHILD',
          });
          break;
        case 'spouse':
        case 'married':
          // Bidirectional relationship: both characters are spouses
          // Add connection to both characters
          if (!connectionsByCharId.has(conn.to)) {
            connectionsByCharId.set(conn.to, []);
          }
          connectionsByCharId.get(conn.to).push({
            _id: `${conn.from}-${conn.to}-${conn.id || Date.now()}`,
            targetCharacterId: conn.from,
            targetCharacterName: fromChar.displayName || fromChar.name || 'Unknown',
            relationToSelf: 'SPOUSE',
          });
          if (!connectionsByCharId.has(conn.from)) {
            connectionsByCharId.set(conn.from, []);
          }
          connectionsByCharId.get(conn.from).push({
            _id: `${conn.to}-${conn.from}-${conn.id || Date.now()}`,
            targetCharacterId: conn.to,
            targetCharacterName: toChar.displayName || toChar.name || 'Unknown',
            relationToSelf: 'SPOUSE',
          });
          break;
        default:
          // For unknown types, try to use the type as-is (uppercase)
          relationToSelf = conn.type?.toUpperCase() || '';
          if (relationToSelf) {
            if (!connectionsByCharId.has(conn.to)) {
              connectionsByCharId.set(conn.to, []);
            }
            connectionsByCharId.get(conn.to).push({
              _id: `${conn.from}-${conn.to}-${conn.id || Date.now()}`,
              targetCharacterId: conn.from,
              targetCharacterName: fromChar.displayName || fromChar.name || 'Unknown',
              relationToSelf,
            });
          }
      }
    });
  }

  // Transform characters to FamilyTree format
  return characters.map((char) => ({
    _id: char.id, // Convert id to _id
    name: char.displayName || char.name || 'Unnamed Character',
    connections: connectionsByCharId.get(char.id) || [],
    // Preserve original character for logging
    __original: char,
  }));
}

const FamilyTreeModal = ({ open, onClose, familyId, centerOnCharacterId }) => {
  const { colors } = useThemeStyles();
  const family = useSelector(selectFamilyById(familyId));
  const familyCharacters = useSelector(selectCharactersByFamily(familyId));

  const transformedCharacters = useMemo(() => {
    return transformCharactersForTree(familyCharacters, family);
  }, [familyCharacters, family]);

  // Map the app's character ID to the transformed _id format
  const centerOnTransformedId = useMemo(() => {
    if (!centerOnCharacterId || !transformedCharacters.length) {
      console.log('No centerOnCharacterId or no characters:', {
        centerOnCharacterId,
        characterCount: transformedCharacters.length,
      });
      return null;
    }
    // Find the character that matches the clicked character's ID
    const transformed = transformedCharacters.find(
      (c) => c.__original?.id === centerOnCharacterId || c._id === centerOnCharacterId
    );
    console.log('Mapping character ID:', {
      centerOnCharacterId,
      found: !!transformed,
      transformedId: transformed?._id,
      originalId: transformed?.__original?.id,
      characterName: transformed?.name,
      allIds: transformedCharacters.map((c) => ({
        original: c.__original?.id,
        transformed: c._id,
        name: c.name,
      })),
    });
    if (!transformed) {
      console.warn('Could not find character to center on!', {
        centerOnCharacterId,
        availableIds: transformedCharacters.map((c) => c.__original?.id),
      });
    }
    return transformed?._id || null;
  }, [centerOnCharacterId, transformedCharacters]);

  // Add a key to force re-render when modal opens with a new character to center on
  const treeKey = useMemo(() => {
    return open ? `${familyId}-${centerOnTransformedId || 'default'}` : 'closed';
  }, [open, familyId, centerOnTransformedId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '90vw',
          height: '85vh',
          maxWidth: 'none',
          backgroundColor: colors.paper.background,
          color: colors.text.primary,
        },
      }}
    >
      <DialogContent
        sx={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {open && (
          <FamilyTreeView
            key={treeKey}
            characters={transformedCharacters}
            showSpouses
            onClose={onClose}
            title={family?.familyName || 'Family Tree'}
            centerOnCharacterId={centerOnTransformedId}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

FamilyTreeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  familyId: PropTypes.string,
  centerOnCharacterId: PropTypes.string,
};

FamilyTreeModal.defaultProps = {
  familyId: null,
  centerOnCharacterId: null,
};

export default FamilyTreeModal;
